import type { AlgorithmResult, SflagaEnhancementOptions } from '../types';
import {
  RESULT_ALGORITHM_NAMES,
  getResultAlgorithmKeys,
  type ResultAlgorithmKey,
} from '../utils/algorithmRegistry';
import type { SharedBenchmarkParameters } from '../utils/parameterUtils';
import type { BenchmarkOperationCounts } from '../utils/benchmarkInstrumentation';
import type {
  BenchmarkWorkerCompleteMessage,
  BenchmarkWorkerConvergencePoint,
  BenchmarkWorkerInitMessage,
  BenchmarkWorkerOutboundMessage,
  BenchmarkWorkerProgressMessage,
  BenchmarkWorkerRunMessage,
} from '../types/benchmarkWorker';

type WorkerCallbacks = {
  onMessage: (event: MessageEvent<BenchmarkWorkerOutboundMessage>) => void;
  onError: (event: ErrorEvent) => void;
};

interface ParallelBenchmarkRunOptions {
  matrix: number[][][];
  taskCount: number;
  serviceCount: number;
  includeSflaGaBaseline: boolean;
  sharedParameters: SharedBenchmarkParameters;
  runSeed: number;
  matrixSeed: number | null;
  sflagaEnhancements: SflagaEnhancementOptions;
  onAlgorithmStart?: (algorithmKey: ResultAlgorithmKey) => void;
  onGenerationUpdate?: (
    algorithmKey: ResultAlgorithmKey,
    generation: number,
    fitness: number
  ) => void;
  onAlgorithmComplete?: (
    algorithmKey: ResultAlgorithmKey,
    result: ParallelBenchmarkAlgorithmRun
  ) => void;
  shouldCancel?: () => boolean;
}

interface WorkerJob {
  algorithmKey: ResultAlgorithmKey;
  requestId: string;
}

export interface ParallelBenchmarkAlgorithmRun {
  result: AlgorithmResult;
  operations: BenchmarkOperationCounts;
  convergence: BenchmarkWorkerConvergencePoint[];
}

const DEFAULT_PARALLEL_WORKERS = 3;
const LARGE_MATRIX_CELL_THRESHOLD = 8_000_000;

export class ParallelBenchmarkRunner {
  private activeWorkers = new Set<Worker>();

  cancel(): void {
    this.terminateWorkers();
  }

  async run({
    matrix,
    taskCount,
    serviceCount,
    includeSflaGaBaseline,
    sharedParameters,
    runSeed,
    matrixSeed,
    sflagaEnhancements,
    onAlgorithmStart,
    onGenerationUpdate,
    onAlgorithmComplete,
    shouldCancel,
  }: ParallelBenchmarkRunOptions): Promise<
    Partial<Record<ResultAlgorithmKey, ParallelBenchmarkAlgorithmRun>>
  > {
    const algorithmKeys = getResultAlgorithmKeys(includeSflaGaBaseline);
    const partialResults: Partial<Record<ResultAlgorithmKey, ParallelBenchmarkAlgorithmRun>> =
      {};
    const jobs = algorithmKeys.map<WorkerJob>((algorithmKey, index) => ({
      algorithmKey,
      requestId: `${algorithmKey}-${runSeed}-${index}`,
    }));

    const workerCount = this.getWorkerCount(jobs.length, taskCount, serviceCount);
    const workers = await this.createWorkers(
      workerCount,
      matrix,
      taskCount,
      serviceCount,
      matrixSeed
    );

    return new Promise((resolve, reject) => {
      const callbacks = new Map<Worker, WorkerCallbacks>();
      let nextJobIndex = 0;
      let completedJobs = 0;
      let settled = false;

      const finishWithError = (error: unknown) => {
        if (settled) {
          return;
        }

        settled = true;
        this.detachWorkerCallbacks(callbacks);
        this.terminateWorkers();
        reject(error instanceof Error ? error : new Error(String(error)));
      };

      const finishWithResults = () => {
        if (settled) {
          return;
        }

        settled = true;
        this.detachWorkerCallbacks(callbacks);
        this.terminateWorkers();
        resolve(this.buildResults(algorithmKeys, partialResults));
      };

      const assignNextJob = (worker: Worker) => {
        if (settled) {
          return;
        }

        if (shouldCancel?.()) {
          finishWithError(new Error('Benchmark cancelled'));
          return;
        }

        const job = jobs[nextJobIndex++];
        if (!job) {
          return;
        }

        onAlgorithmStart?.(job.algorithmKey);

        const runMessage: BenchmarkWorkerRunMessage = {
          type: 'run',
          requestId: job.requestId,
          algorithmKey: job.algorithmKey,
          sharedParameters,
          runSeed,
          ...(job.algorithmKey === 'sflaga'
            ? {
                sflagaEnhancements,
              }
            : {}),
        };

        worker.postMessage(runMessage);
      };

      workers.forEach((worker) => {
        const onMessage = (event: MessageEvent<BenchmarkWorkerOutboundMessage>) => {
          if (settled) {
            return;
          }

          if (shouldCancel?.()) {
            finishWithError(new Error('Benchmark cancelled'));
            return;
          }

          switch (event.data.type) {
            case 'progress':
              this.handleProgressMessage(event.data, onGenerationUpdate);
              break;
            case 'complete':
              this.handleCompleteMessage(
                event.data,
                partialResults,
                onAlgorithmComplete
              );
              completedJobs += 1;

              if (completedJobs >= jobs.length) {
                finishWithResults();
                return;
              }

              assignNextJob(worker);
              break;
            case 'error':
              finishWithError(
                new Error(event.data.error || 'Parallel benchmark worker failed')
              );
              break;
            case 'ready':
            default:
              break;
          }
        };

        const onError = (event: ErrorEvent) => {
          finishWithError(new Error(event.message || 'Parallel benchmark worker crashed'));
        };

        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        callbacks.set(worker, { onMessage, onError });
      });

      workers.forEach((worker) => {
        assignNextJob(worker);
      });
    });
  }

  private handleProgressMessage(
    message: BenchmarkWorkerProgressMessage,
    onGenerationUpdate?: (
      algorithmKey: ResultAlgorithmKey,
      generation: number,
      fitness: number
    ) => void
  ): void {
    onGenerationUpdate?.(message.algorithmKey, message.generation, message.fitness);
  }

  private handleCompleteMessage(
    message: BenchmarkWorkerCompleteMessage,
    partialResults: Partial<Record<ResultAlgorithmKey, ParallelBenchmarkAlgorithmRun>>,
    onAlgorithmComplete?: (
      algorithmKey: ResultAlgorithmKey,
      result: ParallelBenchmarkAlgorithmRun
    ) => void
  ): void {
    const completedRun: ParallelBenchmarkAlgorithmRun = {
      result: message.result,
      operations: message.operations,
      convergence: message.convergence,
    };

    partialResults[message.algorithmKey] = completedRun;
    onAlgorithmComplete?.(message.algorithmKey, completedRun);
  }

  private buildResults(
    algorithmKeys: ResultAlgorithmKey[],
    partialResults: Partial<Record<ResultAlgorithmKey, ParallelBenchmarkAlgorithmRun>>
  ): Partial<Record<ResultAlgorithmKey, ParallelBenchmarkAlgorithmRun>> {
    algorithmKeys.forEach((algorithmKey) => {
      if (!partialResults[algorithmKey]) {
        throw new Error(
          `Parallel benchmark did not return a result for ${RESULT_ALGORITHM_NAMES[algorithmKey]}`
        );
      }
    });

    return partialResults;
  }

  private getWorkerCount(jobCount: number, taskCount: number, serviceCount: number): number {
    if (jobCount <= 1) {
      return 1;
    }

    const hardwareConcurrency =
      typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency)
        ? navigator.hardwareConcurrency
        : 2;

    let workerCap = Math.max(
      2,
      Math.min(DEFAULT_PARALLEL_WORKERS, Math.max(2, hardwareConcurrency - 1))
    );

    const estimatedMatrixCells = taskCount * serviceCount * 9;
    if (estimatedMatrixCells >= LARGE_MATRIX_CELL_THRESHOLD) {
      workerCap = Math.min(workerCap, 2);
    }

    return Math.min(jobCount, workerCap);
  }

  private async createWorkers(
    count: number,
    matrix: number[][][],
    taskCount: number,
    serviceCount: number,
    matrixSeed: number | null
  ): Promise<Worker[]> {
    const workers = Array.from(
      { length: count },
      () => new Worker(new URL('../workers/benchmarkWorker.ts', import.meta.url))
    );

    workers.forEach((worker) => this.activeWorkers.add(worker));

    try {
      await Promise.all(
        workers.map((worker) =>
          this.initializeWorker(worker, matrix, taskCount, serviceCount, matrixSeed)
        )
      );
    } catch (error) {
      this.terminateWorkers();
      throw error;
    }

    return workers;
  }

  private initializeWorker(
    worker: Worker,
    matrix: number[][][],
    taskCount: number,
    serviceCount: number,
    matrixSeed: number | null
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const onMessage = (event: MessageEvent<BenchmarkWorkerOutboundMessage>) => {
        if (event.data.type === 'ready') {
          cleanup();
          resolve();
          return;
        }

        if (event.data.type === 'error') {
          cleanup();
          reject(new Error(event.data.error || 'Failed to initialize benchmark worker'));
        }
      };

      const onError = (event: ErrorEvent) => {
        cleanup();
        reject(new Error(event.message || 'Failed to initialize benchmark worker'));
      };

      const cleanup = () => {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };

      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);

      const initMessage: BenchmarkWorkerInitMessage = {
        type: 'init',
        matrix,
        taskCount,
        serviceCount,
        matrixSeed: matrixSeed ?? undefined,
      };

      worker.postMessage(initMessage);
    });
  }

  private detachWorkerCallbacks(callbacks: Map<Worker, WorkerCallbacks>): void {
    callbacks.forEach(({ onMessage, onError }, worker) => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    });
  }

  private terminateWorkers(): void {
    this.activeWorkers.forEach((worker) => {
      worker.terminate();
    });

    this.activeWorkers.clear();
  }
}
